import { computed, InjectionKey } from "vue"

import { createStore, useStore as baseUseStore, Store } from "vuex"

import {
  allProjectsQuery,
  GroupProjectPreviewData,
  KirbyAllProjectsData,
  ProjectPreviewData,
} from "@/queries/projects"

import { PathUtils } from "@/utils/path.utils"
import { resolveStudentName } from "@/utils/student"
import { KirbyStudentPreviewData } from "@/queries/student"
import { KirbyQuerySchema } from "@/queries/kql"

export interface ProjectsStoreState {
  projects: ProjectPreviewData[]
  students: ProjectPreviewData[]
  activeProject: ProjectPreviewData | null
}

const API = PathUtils.strip(import.meta.env.VITE_KIRBY_URL) + "/api/query"

export const fetchAllProjects = async () => {
  const response = await fetch(API, {
    method: "post",
    body: JSON.stringify(allProjectsQuery),
  }).catch(e => {
    throw new Error("Failed to fetch Kirby JSON")
  })

  const json = await response.json().catch(e => {
    throw new Error("Failed to parse Kirby JSON")
  })

  const siteProjects = json.result as KirbyAllProjectsData

  siteProjects.groupProjects.forEach(group => {
    group.name = resolveStudentName(group)
    // Remove line breaks from group.name
    group.name = group.name.replace(/(\r\n|\n|\r)/gm, " ")
  })

  return [...siteProjects.projects, ...siteProjects.groupProjects]
}

export const fetchStudentPreviews = async (
  ids: string[],
  query: KirbyQuerySchema["select"],
) => {
  if (ids.length === 0) return []
  const idsString = ids.map(id => `"${id}"`).join(", ")
  const response = await fetch(API, {
    method: "post",
    body: JSON.stringify({
      query: `site.find([${idsString}])`,
      select: query,
    }),
  }).catch(e => {
    throw new Error("Failed to fetch Kirby JSON")
  })
  const json = await response.json().catch(e => {
    throw new Error("Failed to parse Kirby JSON")
  })
  const projects = json.result as KirbyStudentPreviewData[]
  return projects
}

export const projectsKey: InjectionKey<Store<ProjectsStoreState>> = Symbol()
export const projectsStore = createStore<ProjectsStoreState>({
  state() {
    return {
      projects: [],
      students: [],
      activeProject: null,
    }
  },
  actions: {
    async fetchProjects({ commit }) {
      const allProjects = await fetchAllProjects()
      commit("setProjects", allProjects)
      commit("setStudents", allProjects)
    },

    async waitForProjects({ state }) {
      // Return a promise that resolves when projects are loaded
      return new Promise<ProjectPreviewData[]>(resolve => {
        // If projects are already loaded, resolve immediately
        if (state.projects.length > 0) {
          resolve(state.projects)
          return
        }

        // Otherwise set up a watcher to check when loading is done
        const unsubscribe = projectsStore.subscribe((mutation, state) => {
          if (state.projects.length > 0) {
            unsubscribe() // Stop watching for mutations
            resolve(state.projects)
          }
        })
      })
    },
  },

  mutations: {
    updateProjectsWithURI(
      state,
      data: { uri: string; data: Partial<ProjectPreviewData> },
    ) {
      const projects = state.students.filter(p => p.uri === data.uri)
      projects.forEach(project => {
        Object.assign(project, data.data)
      })
    },
    updateProject(
      state,
      data: {
        id: string
        data: Partial<ProjectPreviewData>
      },
    ) {
      const project = state.projects.find(p => p.id === data.id)
      if (project) {
        Object.assign(project, data.data)
      }
    },
    setProjects(state, projects: ProjectPreviewData[]) {
      state.projects = projects
    },
    setStudents(state, projects: ProjectPreviewData[]) {
      // This turns all group projects into individual student projects
      const students: ProjectPreviewData[] = []
      const processedStudentIds = new Set<string>() // Track which students we've already processed

      // First process all group projects
      projects.forEach(project => {
        const isGroupProject = project.id.startsWith("programmes/")
        if (isGroupProject) {
          const projectStudents = (project as GroupProjectPreviewData).students
          if (projectStudents.length > 0) {
            const hasCustomNames = !!(project as GroupProjectPreviewData)
              .customNames

            if (hasCustomNames) {
              students.push({ ...project, isPartOfGroup: true })
              projectStudents.forEach(s => processedStudentIds.add(s.id))
            } else {
              projectStudents.forEach(student => {
                const originalStudentEntry = projects.find(
                  p => p.id === student.id,
                )
                if (originalStudentEntry) {
                  const studentCopy: ProjectPreviewData = {
                    ...originalStudentEntry,
                    id: student.id,
                    uri: project.uri,
                    name: originalStudentEntry.name,
                    title: project.title,
                    hasThumbnail: project.hasThumbnail,
                    isPartOfGroup: true,
                  }
                  students.push(studentCopy)
                  processedStudentIds.add(student.id)
                }
              })
            }
          } else {
            // If there are no students, still add the group project itself
            // const groupProjectCopy: ProjectPreviewData = {
            //   ...project,
            //   id: project.id,
            //   uri: project.uri,
            //   name:
            //     (project as GroupProjectPreviewData).customNames ||
            //     project.name ||
            //     project.title,
            //   hasThumbnail: project.hasThumbnail,
            //   isPartOfGroup: true, // Mark as a group project
            // }
            // students.push(groupProjectCopy)
            // processedStudentIds.add(project.id) // Mark the group project as processed
          }
        }
      })

      // Then add any individual projects that aren't part of a group
      projects.forEach(project => {
        const isGroupProject = project.id.startsWith("programmes/")
        if (!isGroupProject && !processedStudentIds.has(project.id)) {
          students.push(project)
        }
      })

      state.students = students
    },
    setActiveProject(state, project: ProjectPreviewData | null) {
      state.activeProject = project
    },
  },

  getters: {
    getProjects(state) {
      return state.projects
    },
    getActiveProject(state) {
      return state.activeProject
    },
    getVisibleProjects(state) {
      return state.projects.filter(project => project.hasThumbnail)
    },
    getVisibleStudents(state) {
      return state.students.filter(project => project.hasThumbnail)
    },
  },
})

export function useProjectsStore() {
  return baseUseStore(projectsKey)
}

function buildAlphaIndex(
  items: ProjectPreviewData[],
): Record<string, ProjectPreviewData[]> {
  const groups = items.reduce(
    (acc, item) => {
      if (!item.name) return acc
      // Decompose accented letters (e.g. "Í" → "I" + combining mark) and strip
      // the marks so names like "Ísafold" group under the base Latin letter.
      const base = item.name[0]
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toUpperCase()
      const letter = /^[A-Z]$/.test(base) ? base : "#"
      ;(acc[letter] ??= []).push(item)
      return acc
    },
    {} as Record<string, ProjectPreviewData[]>,
  )
  for (const key in groups)
    groups[key].sort((a, b) => a.name.localeCompare(b.name))
  return Object.fromEntries(
    Object.keys(groups)
      .sort((a, b) => (a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b)))
      .map(k => [k, groups[k]]),
  )
}

// Utility function to wait for projects to be loaded
export async function waitForProjects(): Promise<ProjectPreviewData[]> {
  return projectsStore.dispatch("waitForProjects")
}

export function useProjects() {
  const projects = computed<ProjectPreviewData[]>(
    () => projectsStore.getters.getProjects,
  )

  const isLoading = computed(() => projects.value.length === 0)

  // Load projects and track local loading state
  async function loadProjects() {
    if (projects.value.length > 0) return projects.value
    try {
      await projectsStore.dispatch("fetchProjects")
    } catch (error) {
      console.error("Error loading projects:", error)
    }
    return projects.value
  }

  return {
    projects,
    isLoading,
    loadProjects,
    activeProject: computed(() => projectsStore.getters.getActiveProject),
    visibleProjects: computed<ProjectPreviewData[]>(
      () => projectsStore.getters.getVisibleProjects,
    ),
    visibleStudents: computed<ProjectPreviewData[]>(
      () => projectsStore.getters.getVisibleStudents,
    ),
    projectIndex: computed(() =>
      buildAlphaIndex(projectsStore.getters.getVisibleProjects),
    ),
    studentIndex: computed(() =>
      buildAlphaIndex(projectsStore.getters.getVisibleStudents),
    ),
  }
}

export function getProjectById(id: string): ProjectPreviewData | null {
  const projects: ProjectPreviewData[] = projectsStore.getters.getProjects
  return projects.find(project => project.id === id) || null
}
